import { isProductionKitchen } from '@gozoki/api-types'
import { useQuery } from '@tanstack/react-query'
import { Input } from '~/components/ui/input'
import { collaboratorsQueryOptions } from '~/lib/collaborators-query'
import { api } from '~/lib/tuyau'
import { getTuyauErrorMessage } from '~/lib/utils'
import { NUMERIC_FIELD_ERROR } from './logistics-numbers'
import type { HomeKind, HomeSelection } from './logistics-places'

/**
 * Champ numérique saisi en texte, avec son message d'erreur SOUS le champ : une saisie illisible
 * se voit, elle n'est jamais convertie en « non renseigné » dans le dos de l'utilisateur.
 */
export const NumberField = ({
  className,
  placeholder,
  value,
  invalid,
  onChange,
}: {
  className?: string
  placeholder: string
  value: string
  invalid: boolean
  onChange: (value: string) => void
}) => (
  <div className="flex flex-col gap-1">
    {/* Le placeholder disparaît une fois le champ rempli : il sert aussi de libellé accessible. */}
    <Input
      className={className}
      placeholder={placeholder}
      aria-label={placeholder}
      aria-invalid={invalid}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
    {invalid ? <p className="text-xs text-destructive">{NUMERIC_FIELD_ERROR}</p> : null}
  </div>
)

/**
 * `<select>` natif aux couleurs du thème, repris de l'ancienne page de test avec le formulaire du
 * bloc Chauffeurs (onglet RH). Dette assumée, hors stack canonique (`Select` de
 * `@gozoki/ui`, TanStack Form, `AppTable`) : cf. « Dette connue » du plan simulateur.
 */
export const NativeSelect = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" {...props} />
)

/** Les deux sélecteurs du rattachement : le type de base, puis la base elle-même. */
export const HomeSelector = ({
  kitchens,
  hubs,
  value,
  onChange,
}: {
  kitchens: { id: number; label: string }[]
  hubs: { id: number; name: string }[]
  value: HomeSelection
  onChange: (value: HomeSelection) => void
}) => {
  const options =
    value.homeKind === 'kitchen' ? kitchens : hubs.map((hub) => ({ id: hub.id, label: hub.name }))

  return (
    <>
      <NativeSelect
        value={value.homeKind}
        onChange={(event) => onChange({ homeKind: event.target.value as HomeKind, homeId: '' })}
      >
        <option value="kitchen">Rattaché à un atelier</option>
        <option value="hub">Rattaché à un hub</option>
      </NativeSelect>
      <NativeSelect
        value={value.homeId}
        onChange={(event) => onChange({ ...value, homeId: event.target.value })}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </NativeSelect>
    </>
  )
}

/** Message d'erreur du backend, affiché tel quel sous le bouton qui l'a déclenché. */
export const MutationError = ({ error }: { error: unknown }) =>
  error ? (
    <p className="text-sm whitespace-pre-wrap text-destructive">{getTuyauErrorMessage(error)}</p>
  ) : null

/**
 * Échec d'une lecture (403, 500…) : sans ce message, un tableau ou un sélecteur en erreur
 * ressemblerait à une liste vide.
 */
export const QueryError = ({ error, label }: { error: unknown; label: string }) =>
  error ? (
    <p className="text-sm whitespace-pre-wrap text-destructive">
      {label} : {getTuyauErrorMessage(error)}
    </p>
  ) : null

/** Libellé de l'erreur des ateliers, lus dans le configurateur industriel (flag et droit à part). */
export const KITCHENS_UNAVAILABLE_LABEL =
  'Ateliers indisponibles (configurateur industriel : flag « configurateur-industriel » et droit prévisions requis)'

export const Section = ({ title, children }: React.PropsWithChildren<{ title: string }>) => (
  <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4">
    <h2 className="text-lg font-semibold">{title}</h2>
    {children}
  </section>
)

export const Th = ({ children }: React.PropsWithChildren) => (
  <th className="border border-border px-2 py-1 text-left font-medium">{children}</th>
)

export const Td = ({ children }: React.PropsWithChildren) => (
  <td className="border border-border px-2 py-1 align-top">{children}</td>
)

export const DataTable = ({ children }: React.PropsWithChildren) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-xs">{children}</table>
  </div>
)

/**
 * Le configurateur industriel (ateliers, boutiques) change rarement et coûte plusieurs requêtes
 * côté API : gardé frais cinq minutes, par les onglets référentiels comme par la modale de
 * création d'une tournée.
 */
const CONFIGURATOR_STALE_TIME_MS = 5 * 60 * 1000

/** Lecture du configurateur industriel, commune aux onglets référentiels et à la modale. */
export const configuratorQueryOptions = ({ enabled = true }: { enabled?: boolean } = {}) =>
  api.industrialConfigurator.index.queryOptions(
    {},
    { enabled, staleTime: CONFIGURATOR_STALE_TIME_MS },
  )

/**
 * Ateliers et collaborateurs des onglets référentiels. Les ateliers viennent du configurateur
 * industriel, seul endpoint du BO qui rend leur liste déjà étiquetée : il
 * dépend du flag `configurateur-industriel` et du droit prévisions, les collaborateurs du droit RH
 * de lecture — un profil purement logistique peut donc se voir refuser l'une ou l'autre liste.
 * Les erreurs sont rendues pour être affichées (des sélecteurs vides sans explication laisseraient
 * croire à un référentiel vide). Chaque lecture ne part que si l'onglet affiché s'en sert : la
 * liste des collaborateurs, lourde, n'est utile qu'à l'onglet RH.
 */
export const useReferenceLists = ({
  configurator: withConfigurator = true,
  collaborators: withCollaborators = true,
} = {}) => {
  const configurator = useQuery(configuratorQueryOptions({ enabled: withConfigurator }))
  // Liste lourde : même cache que les écrans RH (5 min), pas de rechargement à chaque fiche.
  const collaborators = useQuery(
    api.collaborators.getAll.queryOptions(
      {},
      { ...collaboratorsQueryOptions, enabled: withCollaborators },
    ),
  )

  return {
    // Ateliers seulement : les fournisseurs de négoce ne portent ni hub, ni véhicule, ni chauffeur.
    kitchens: (configurator.data?.kitchens ?? []).filter(isProductionKitchen),
    collaborators: collaborators.data ?? [],
    configuratorError: configurator.error,
    configuratorStatus: configurator.status,
    collaboratorsError: collaborators.error,
  }
}
